import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { CheckCircle, Loader2, XCircle } from "lucide-react";

export default function MembershipSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const paypalSubId = searchParams.get("paypal_subscription_id") || searchParams.get("subscription_id");
  const [status, setStatus] = useState("loading"); // loading, success, failed

  useEffect(() => {
    // PayPal: activation already attempted on the buttons page.
    if (paypalSubId) {
      axios.post(`${API}/memberships/paypal/${paypalSubId}/activate`).catch(() => {});
      setStatus("success");
      return;
    }
    if (!sessionId) { setStatus("failed"); return; }

    const poll = async (attempts = 0) => {
      const maxAttempts = 8;
      if (attempts >= maxAttempts) { setStatus("failed"); return; }
      try {
        const res = await axios.get(`${API}/memberships/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setStatus("success"); return; }
        if (res.data.status === "expired") { setStatus("failed"); return; }
        setTimeout(() => poll(attempts + 1), 2000);
      } catch {
        setTimeout(() => poll(attempts + 1), 2000);
      }
    };
    poll();
  }, [sessionId, paypalSubId]);

  return (
    <div className="page-transition pt-[72px] min-h-screen bg-[#0c0c0c] flex items-center justify-center" data-testid="membership-success-page">
      <div className="container-custom max-w-xl text-center py-20">
        {status === "loading" && (
          <div className="animate-fade-in">
            <Loader2 className="animate-spin text-[#b8966a] mx-auto mb-6" size={48} />
            <h1 className="font-heading text-2xl text-[#f8f5f0] mb-4">Activating Your Membership</h1>
            <p className="font-body text-[#a19d98]">Please wait while we confirm your subscription…</p>
          </div>
        )}

        {status === "success" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-6 border border-[#b8966a] rounded-full flex items-center justify-center">
              <CheckCircle className="text-[#b8966a]" size={40} />
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-[#f8f5f0] mb-4">Welcome to Fleuris Noir</h1>
            <p className="font-body text-lg text-[#a19d98] mb-8">
              Your membership is active. We'll be in touch shortly to schedule your first delivery — and a confirmation is on its way to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/collection" className="btn-secondary" data-testid="ms-continue">Explore Collection</Link>
              <Link to="/" className="btn-primary" data-testid="ms-home">Return Home</Link>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-6 border border-red-500/50 rounded-full flex items-center justify-center">
              <XCircle className="text-red-500" size={40} />
            </div>
            <h1 className="font-display text-3xl text-[#f8f5f0] mb-4">Membership Not Confirmed</h1>
            <p className="font-body text-[#a19d98] mb-8">
              We couldn't confirm your subscription. This may be due to a cancelled checkout. Please try again or contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/memberships" className="btn-secondary" data-testid="ms-retry">Try Again</Link>
              <Link to="/inquiries" className="btn-primary" data-testid="ms-contact">Contact Us</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
